import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useApp } from "../AppContext";
import { useAuth } from "../AuthContext";
import { esportaFile } from "../api";
import { MODULI, moduloDaPercorso } from "../moduli";
import { useFinestraRighe } from "../useFinestraRighe";
import CorreggiCodiciModal from "../CorreggiCodiciModal";
import DuplicatiModal from "../DuplicatiModal";
import LegendaFunzioniModal, { FUNZIONI_REVISIONE } from "../LegendaFunzioniModal";

// Il motore di import dell'ERP carica un file alla volta e quel file può
// contenere un solo articolo/variante/combinazione. Se le righe mappate
// coprono più combinazioni bisogna quindi generare un file separato per
// ciascuna, altrimenti l'import fallirebbe o mischierebbe articoli diversi.
// La combinazione è identificata da articolo + variante + colore (il modo in
// cui in questo tracciato si esprime la "combinazione" di un articolo).
const CHIAVI_COMBINAZIONE = ["codice_articolo_pf", "codice_variante_pf", "codice_colore_pf"];

// Colonne del tracciato SISC Distinta Base che identificano un materiale: se
// TUTTE e sei sono identiche tra due o più righe, il materiale è "duplicato
// esatto" — vedi il blocco "righe duplicate" più sotto. Non scatta sul
// tracciato DDT (non ha queste colonne): il controllo si attiva solo se
// tutte e sei sono presenti nel tracciato corrente.
const CHIAVI_DUPLICATO = ["codice_materiale", "descrizione_materiale", "codice_colore", "descrizione_colore", "um", "consumo"];

function raggruppaPerCombinazione(righe) {
  const gruppi = new Map();
  for (const riga of righe) {
    const parti = CHIAVI_COMBINAZIONE.map((k) => String(riga[k] ?? "").trim());
    const chiave = parti.join("||");
    if (!gruppi.has(chiave)) {
      const etichetta = parti.filter(Boolean).join("_") || "senza_combinazione";
      gruppi.set(chiave, { etichetta, righe: [] });
    }
    gruppi.get(chiave).righe.push(riga);
  }
  return [...gruppi.values()];
}

export default function Revisione() {
  const navigate = useNavigate();
  const location = useLocation();
  const modulo = moduloDaPercorso(location.pathname);
  const percorsoBase = MODULI[modulo].percorsoBase;
  const { tracciato, fileCaricato, lottoPdf, righeMappate, setRigheMappate, resetFlusso, templateAttivo } = useApp();
  const { puoMappare } = useAuth();

  const [soloMappate, setSoloMappate] = useState(false);
  // Filtro di ricerca per colonna: { chiave: testo }. Solo visuale — non
  // tocca righeMappate, quindi non interferisce con "↓ riempi"/AA/aa/export
  // (che lavorano sempre su TUTTE le righe, filtrate o no) — serve solo a
  // ritrovare in fretta delle righe in una tabella lunga.
  const [filtriColonna, setFiltriColonna] = useState({});
  const [nomeFile, setNomeFile] = useState(lottoPdf?.file ? "distinte" : (fileCaricato?.nomeOriginale?.replace(/\.[^.]+$/, "") || "export"));
  const [esportando, setEsportando] = useState(false);
  const [errore, setErrore] = useState("");
  const [messaggioCartella, setMessaggioCartella] = useState("");
  // Riferimenti diretti agli <input> della tabella (vedi onIncollaCella/
  // riempiColonna più sotto): il hook va chiamato qui, PRIMA dell'eventuale
  // "return null" più in basso se righeMappate/tracciato non sono ancora
  // pronti — mai dopo un return anticipato.
  const celleRef = useRef({});
  // Chiave stabile per riga, usata SOLO per <tr key=...>: dopo un'eliminazione
  // (vedi eliminaRighe più sotto) l'indice delle righe rimaste slitta, e con
  // <tr key={indice}> React riuserebbe il <tr> vecchio mostrando negli <input>
  // (non controllati, defaultValue) il valore della riga tolta invece di
  // quello vero. Si assegna quindi un "__chiaveRiga" DIRETTAMENTE sull'oggetto
  // riga (mutandolo, invece di una mappa esterna) — stessa idea di
  // "__chiaveRiga" in OrdiniMapping.jsx (anteprima ordini) — proprio perché
  // modificaCella crea un nuovo oggetto riga ad ogni modifica (spread): una
  // mappa esterna indicizzata per identità dell'oggetto perderebbe la chiave
  // ad ogni modifica, rimontando l'intera riga (e quindi perdendo il fuoco,
  // es. durante Tab tra celle) anche per una singola modifica di cella. Una
  // proprietà sull'oggetto invece sopravvive allo spread. Non finisce
  // nell'export: routes/api.js/creaBufferXlsx legge solo le colonne note dal
  // tracciato (tracciato.colonne), ignorando ogni proprietà extra.
  const prossimaChiaveRiga = useRef(0);
  function chiaveRiga(riga) {
    if (riga.__chiaveRiga === undefined) riga.__chiaveRiga = prossimaChiaveRiga.current++;
    return riga.__chiaveRiga;
  }

  useEffect(() => {
    if (!righeMappate || !tracciato) navigate(percorsoBase);
  }, [righeMappate, tracciato, navigate, percorsoBase]);

  const gruppi = useMemo(() => (righeMappate ? raggruppaPerCombinazione(righeMappate) : []), [righeMappate]);

  // Colonne obbligatorie per l'ERP (tracciato.json, campo "obbligatoria") con
  // almeno una riga vuota: qui si controllano i VALORI VERI riga per riga
  // (non solo se in Mapping è stata scelta una regola), perché una regola
  // "da colonna sorgente" può comunque risultare vuota su singole righe. Finché
  // ce n'è anche una sola, il download resta bloccato — il template si può
  // comunque salvare da Mapping per riprenderlo più tardi.
  const colonneObbligatorieMancanti = useMemo(() => {
    if (!tracciato || !righeMappate) return [];
    return tracciato.colonne
      .filter((c) => c.obbligatoria)
      .map((c) => {
        const righeVuote = [];
        righeMappate.forEach((r, i) => {
          if (String(r[c.chiave] ?? "").trim() === "") righeVuote.push(i + 1);
        });
        return { ...c, righeVuote };
      })
      .filter((c) => c.righeVuote.length > 0);
  }, [tracciato, righeMappate]);

  const [soloAnomalieCodice, setSoloAnomalieCodice] = useState(false);
  const [mostraCorreggiCodici, setMostraCorreggiCodici] = useState(false);
  const [soloDuplicate, setSoloDuplicate] = useState(false);
  const [mostraDuplicati, setMostraDuplicati] = useState(false);
  const [mostraFunzioni, setMostraFunzioni] = useState(false);
  // Ultime righe eliminate, con la posizione che avevano, per "Annulla
  // eliminazione" (elimina riga non ha conferma: è un colpo solo, e si può
  // sempre rimettere a posto).
  const [ultimaEliminazione, setUltimaEliminazione] = useState(null);

  // Le costanti qui sotto (fino a "finestra" incluso) sono scritte in modo
  // DIFENSIVO (righeMappate/tracciato possono ancora essere null a questo
  // punto) perché useFinestraRighe è un hook e va chiamato SEMPRE, nello
  // stesso ordine ad ogni render — non si può calcolarne l'input solo dopo
  // l'eventuale "return null" più sotto (violerebbe le regole degli hook).
  const colonneMostrate = (tracciato && righeMappate)
    ? (soloMappate
      ? tracciato.colonne.filter((c) => c.obbligatoria || righeMappate.some((r) => String(r[c.chiave] ?? "").trim() !== ""))
      : tracciato.colonne)
    : [];

  // Segnala i "codici" numerici (es. 24,71814 — cifre con UN separatore
  // decimale) con un problema da correggere: lunghezza in cifre diversa dal
  // resto della colonna (capita quando Excel perde uno zero finale nella
  // parte decimale) OPPURE del testo in più dopo il codice (es. "99.718163
  // (cotton)" — un'annotazione che il file cliente porta dietro ma che
  // l'ERP non riconosce come parte del codice articolo). Scatta SOLO se
  // OGNI valore non vuoto della colonna INIZIA con questa forma (solo cifre
  // + un separatore, testo dopo ignorato ai fini del riconoscimento — ma
  // comunque segnalato come anomalia): su una colonna di testo libero
  // (nomi, descrizioni) lunghezze diverse sono normali e non vanno
  // segnalate — vedi la stessa funzionalità in OrdiniMapping.jsx. Calcolato
  // qui, prima del "return null" più sotto, perché serve a "solo righe da
  // correggere", da cui dipende quante righe la tabella deve renderizzare
  // (vedi useFinestraRighe subito sotto).
  function analizzaCodice(valore) {
    const testo = String(valore ?? "").trim();
    const m = testo.match(/^(\d+)[.,](\d+)/);
    if (!m) return null;
    return { cifre: m[1].length + m[2].length, extra: testo.slice(m[0].length).trim() };
  }
  const colonneCodiceNonUniforme = new Map();
  for (const c of colonneMostrate) {
    const infoPerRiga = [];
    let tutteICodici = true;
    for (let i = 0; i < (righeMappate || []).length; i++) {
      const testo = String(righeMappate[i][c.chiave] ?? "").trim();
      if (!testo) continue;
      const info = analizzaCodice(testo);
      if (info === null) { tutteICodici = false; break; }
      infoPerRiga.push({ i, ...info });
    }
    if (!tutteICodici || infoPerRiga.length < 2) continue;
    const conteggio = new Map();
    for (const { cifre } of infoPerRiga) conteggio.set(cifre, (conteggio.get(cifre) || 0) + 1);
    let cifreAttese = null, massimo = 0;
    for (const [cifre, conto] of conteggio) if (conto > massimo) { massimo = conto; cifreAttese = cifre; }
    const righe = new Map();
    for (const { i, cifre, extra } of infoPerRiga) {
      if (cifre !== cifreAttese || extra) righe.set(i, { cifreTrovate: cifre, testoExtra: extra });
    }
    if (righe.size > 0) colonneCodiceNonUniforme.set(c.chiave, { cifreAttese, righe });
  }

  // Unione, su tutte le colonne, delle righe segnalate da 🔢⚠️ — serve per
  // "mostra solo le righe da correggere" qui sotto: su un file da migliaia
  // di righe non si può chiedere di scorrerle tutte per trovare le poche
  // sospette.
  const righeConAnomaliaCodice = new Set();
  for (const { righe } of colonneCodiceNonUniforme.values()) {
    for (const i of righe.keys()) righeConAnomaliaCodice.add(i);
  }

  // Segnala i materiali "duplicati esatti": più righe con lo stesso Codice
  // Materiale, Descrizione Materiale, Codice Colore, Descrizione Colore, UM
  // e Consumo TUTTI uguali. Un materiale che si ripete NON è di per sé un
  // errore (può comparire più volte con un consumo diverso, per parti
  // diverse del prodotto finito) — qui si segnala solo quando è identico su
  // tutte e sei le colonne insieme, il caso che difficilmente è voluto
  // (vedi DuplicatiModal.jsx). Righe ancora senza Codice Materiale (in corso
  // di compilazione) non vengono raggruppate tra loro: sarebbero tutte
  // "vuote uguali" senza che significhi nulla.
  const colonneDuplicatoPresenti = CHIAVI_DUPLICATO.every((k) => tracciato?.colonne?.some((c) => c.chiave === k));
  const gruppiDuplicati = [];
  const righeDuplicate = new Set();
  if (colonneDuplicatoPresenti) {
    const mappaDuplicati = new Map();
    (righeMappate || []).forEach((riga, i) => {
      if (String(riga.codice_materiale ?? "").trim() === "") return;
      const chiave = CHIAVI_DUPLICATO.map((k) => String(riga[k] ?? "").trim().toLowerCase()).join("||");
      if (!mappaDuplicati.has(chiave)) mappaDuplicati.set(chiave, []);
      mappaDuplicati.get(chiave).push(i);
    });
    for (const indici of mappaDuplicati.values()) {
      if (indici.length < 2) continue;
      gruppiDuplicati.push({ indici });
      for (const i of indici) righeDuplicate.add(i);
    }
  }

  // Righe da mostrare dopo i filtri per colonna (+ eventuale "solo righe da
  // correggere"), ma con l'indice ORIGINALE preservato (non quello
  // nell'elenco filtrato): refCella/modificaCella/onIncollaCella si
  // aspettano l'indice vero dentro righeMappate, altrimenti scriverebbero
  // nella riga sbagliata appena un filtro nasconde qualcosa.
  const righeFiltrate = (righeMappate || [])
    .map((riga, i) => ({ riga, i }))
    .filter(({ riga, i }) => {
      if (soloAnomalieCodice && righeConAnomaliaCodice.size > 0 && !righeConAnomaliaCodice.has(i)) return false;
      if (soloDuplicate && righeDuplicate.size > 0 && !righeDuplicate.has(i)) return false;
      return colonneMostrate.every((c) => {
        const filtro = (filtriColonna[c.chiave] || "").trim().toLowerCase();
        if (!filtro) return true;
        return String(riga[c.chiave] ?? "").toLowerCase().includes(filtro);
      });
    });
  const filtroAttivo = Object.values(filtriColonna).some((v) => (v || "").trim() !== "") || soloAnomalieCodice || soloDuplicate;

  // Windowing (vedi useFinestraRighe.js): su un file da migliaia di righe,
  // renderizzare un <input> per ogni cella di ogni riga blocca il browser
  // (misurato: 150.000 celle, oltre 45s per digitare nel filtro). Sotto
  // soglia il comportamento resta identico a prima — vedi la stessa
  // funzionalità/stesso motivo in OrdiniMapping.jsx.
  const contenitoreRef = useRef(null);
  const finestra = useFinestraRighe(contenitoreRef, righeFiltrate.length);

  if (!righeMappate || !tracciato) return null;

  // Le celle sono <input>, non testo semplice: un input non comunica alla
  // tabella quanto è lungo il suo contenuto (a differenza di un <td> di solo
  // testo, come nell'anteprima di calibrazione), quindi senza una larghezza
  // esplicita ogni colonna resta stretta a prescindere da quanto è lungo il
  // valore — una descrizione lunga finisce nascosta, scorribile solo dentro
  // la casella. Si calcola quindi, per ciascuna colonna, la larghezza del
  // valore più lungo tra le righe mostrate (in "ch", un carattere monospace),
  // così la colonna si allarga da sola fino a mostrare tutto il testo — con
  // un tetto per non lasciare che un valore anomalo sfondi la tabella.
  const larghezzeColonne = {};
  for (const c of colonneMostrate) {
    let max = c.nome.length;
    for (const riga of righeMappate) {
      const lunghezza = String(riga[c.chiave] ?? "").length;
      if (lunghezza > max) max = lunghezza;
    }
    larghezzeColonne[c.chiave] = Math.min(90, Math.max(14, max + 2));
  }

  // Segnala le colonne con maiuscole/minuscole non uniformi (es. una riga
  // "MILANO" e un'altra "milano" o "Milano"): capita spesso quando il file
  // cliente arriva compilato da persone diverse. Un valore senza NESSUNA
  // lettera (solo numeri/simboli) non conta: il maiuscolo/minuscolo non ha
  // senso per lui, non deve far scattare l'avviso da solo.
  function profiloMaiuscoleMinuscole(valore) {
    const testo = String(valore ?? "").trim();
    if (!/[a-zà-ÿ]/i.test(testo)) return null;
    const tuttoMaiuscolo = testo === testo.toUpperCase();
    const tuttoMinuscolo = testo === testo.toLowerCase();
    if (tuttoMaiuscolo && !tuttoMinuscolo) return "maiuscolo";
    if (tuttoMinuscolo && !tuttoMaiuscolo) return "minuscolo";
    return "misto";
  }
  const colonneCasoNonUniforme = new Set();
  for (const c of colonneMostrate) {
    const profiliTrovati = new Set();
    for (const riga of righeMappate) {
      const p = profiloMaiuscoleMinuscole(riga[c.chiave]);
      if (p) profiliTrovati.add(p);
    }
    if (profiliTrovati.size > 1) colonneCasoNonUniforme.add(c.chiave);
  }

  function modificaCella(indiceRiga, chiave, valore) {
    setRigheMappate((prev) => {
      const copia = prev.slice();
      copia[indiceRiga] = { ...copia[indiceRiga], [chiave]: valore };
      return copia;
    });
  }

  // Elimina una o più righe (vedi DuplicatiModal.jsx, "🗑️ Elimina" su
  // singola riga o su un gruppo/tutte le righe duplicate insieme). Gli
  // indici passati sono quelli DENTRO righeMappate (non quelli filtrati).
  function eliminaRighe(indici) {
    const daTogliere = indici instanceof Set ? indici : new Set(indici);
    if (daTogliere.size === 0) return;
    setUltimaEliminazione(
      [...daTogliere].sort((a, b) => a - b).filter((i) => i < righeMappate.length).map((i) => ({ i, riga: righeMappate[i] }))
    );
    setRigheMappate((prev) => prev.filter((_, i) => !daTogliere.has(i)));
  }

  // Rimette le righe eliminate per ultime nella posizione di prima (le
  // modifiche fatte nel frattempo alle altre righe restano).
  function annullaEliminazione() {
    if (!ultimaEliminazione) return;
    const daRimettere = ultimaEliminazione;
    setRigheMappate((prev) => {
      const copia = prev.slice();
      for (const { i, riga } of daRimettere) copia.splice(i, 0, riga);
      return copia;
    });
    setUltimaEliminazione(null);
  }

  // refCella/scriviCella: servono per scrivere in celle DIVERSE da quella
  // dove l'utente sta digitando (incolla multi-cella, "riempi colonna" qui
  // sotto). Stessa idea di OrdiniMapping.jsx (sezione Anteprima e
  // importazione): celle non controllate (defaultValue + onBlur) per non
  // ridisegnare tutta la tabella a ogni tasto premuto, quindi quando si
  // scrive in una cella "a distanza" va aggiornato anche il DOM a mano.
  function refCella(indiceRiga, chiave) {
    return (el) => { celleRef.current[`${indiceRiga}|${chiave}`] = el; };
  }
  function scriviCella(indiceRiga, chiave, valore) {
    modificaCella(indiceRiga, chiave, valore);
    const el = celleRef.current[`${indiceRiga}|${chiave}`];
    if (el) el.value = valore;
  }

  // "✏️ Correggi codici" (CorreggiCodiciModal.jsx): applica in blocco una
  // correzione a TUTTE le righe che hanno un certo valore in quella colonna
  // — vedi lo stesso meccanismo in OrdiniMapping.jsx.
  function applicaCorrezioniCodice(colonna, correzioni) {
    const mappa = new Map(correzioni.map((c) => [c.valoreOriginale, c.valoreNuovo]));
    righeMappate.forEach((riga, i) => {
      const attuale = String(riga[colonna] ?? "");
      if (mappa.has(attuale)) scriviCella(i, colonna, mappa.get(attuale));
    });
  }

  // Incolla multi-cella "come Excel": copiando un blocco di celle (da Excel,
  // o da un'altra colonna qui) e incollandolo su una cella di questa tabella,
  // si distribuisce su più righe (a capo) e più colonne (tabulazione) a
  // partire da lì — utile soprattutto per le colonne obbligatorie lasciate
  // "manuale" in Mapping (es. Stagione, diversa a ogni distinta ma non nel
  // file cliente): si incolla una volta la colonna e via, invece di scrivere
  // riga per riga. Su un incolla di una sola cella si lascia fare al browser.
  function onIncollaCella(e, indiceRiga, indiceColonna) {
    const testo = e.clipboardData.getData("text");
    if (!/\r|\n|\t/.test(testo)) return;
    e.preventDefault();
    const righeTesto = testo.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");
    // Excel aggiunge quasi sempre un a-capo finale copiando un blocco di
    // celle: senza toglierlo si "consumerebbe" una riga di tabella in più
    // scrivendoci una stringa vuota inutilmente.
    if (righeTesto.length > 1 && righeTesto[righeTesto.length - 1] === "") righeTesto.pop();
    righeTesto.forEach((rigaTesto, offsetRiga) => {
      const rigaDestinazione = indiceRiga + offsetRiga;
      if (rigaDestinazione >= righeMappate.length) return;
      rigaTesto.split("\t").forEach((valore, offsetColonna) => {
        const colDestinazione = indiceColonna + offsetColonna;
        if (colDestinazione >= colonneMostrate.length) return;
        scriviCella(rigaDestinazione, colonneMostrate[colDestinazione].chiave, valore);
      });
    });
  }

  // Riempie tutta la colonna con il valore della prima riga — comodo per un
  // campo obbligatorio che vale uguale per tutta la distinta (es. la
  // Stagione), senza incollarlo riga per riga.
  function riempiColonna(chiave) {
    const valore = righeMappate[0]?.[chiave] ?? "";
    righeMappate.forEach((_, i) => { if (i > 0) scriviCella(i, chiave, valore); });
  }

  // MAIUSCOLO/minuscolo su tutta la colonna in un colpo solo: molti file
  // cliente arrivano con i dati tutti minuscoli o tutti maiuscoli (a
  // seconda di chi li ha compilati), da uniformare prima dell'export senza
  // doverli riscrivere riga per riga.
  function trasformaColonna(chiave, tipo) {
    righeMappate.forEach((riga, i) => {
      const valoreAttuale = String(riga[chiave] ?? "");
      const valoreNuovo = tipo === "maiuscolo" ? valoreAttuale.toUpperCase() : valoreAttuale.toLowerCase();
      if (valoreNuovo !== valoreAttuale) scriviCella(i, chiave, valoreNuovo);
    });
  }

  async function scarica() {
    setErrore("");
    setMessaggioCartella("");
    if (colonneObbligatorieMancanti.length > 0) {
      setErrore("Ci sono colonne obbligatorie ancora vuote: compilale (vedi sotto) prima di generare il file.");
      return;
    }
    if (righeDuplicate.size > 0) {
      const procedi = window.confirm(
        `Trovate ${righeDuplicate.size} righe duplicate (stesso Codice Materiale, Descrizione, Colore, UM e Consumo) in ${gruppiDuplicati.length} gruppi.\n\n` +
        `Non è per forza un errore, ma controllale prima di esportare.\n\n` +
        `OK per esportare comunque, Annulla per rivedere/eliminare prima le righe duplicate.`
      );
      if (!procedi) {
        setMostraDuplicati(true);
        return;
      }
    }
    setEsportando(true);
    try {
      const gruppiPayload = gruppi.map((g) => ({ suffisso: g.etichetta, righe: g.righe }));
      const { cartellaSalvata, avviso } = await esportaFile({
        nomeFile,
        gruppi: gruppiPayload,
        modulo,
        cartellaDestinazione: templateAttivo?.cartellaDestinazione || "",
      });
      if (avviso) setMessaggioCartella(`⚠️ ${avviso}`);
      else if (cartellaSalvata) setMessaggioCartella(`✅ Copia salvata anche in: ${cartellaSalvata}`);
    } catch (err) {
      setErrore(err.message);
    } finally {
      setEsportando(false);
    }
  }

  function nuovoImport() {
    if (window.confirm("Iniziare un nuovo import? Le modifiche non salvate in questa revisione andranno perse.")) {
      resetFlusso();
      navigate(percorsoBase);
    }
  }

  return (
    <div className="pagina">
      <section className="riquadro barra-azioni">
        <h2>Revisione dati — {righeMappate.length} righe</h2>
        <div className="riga-controlli">
          <label>
            <input type="checkbox" checked={soloMappate} onChange={(e) => setSoloMappate(e.target.checked)} />
            {" "}Mostra solo colonne con dati
          </label>
          <label>Nome file:</label>
          <input type="text" value={nomeFile} onChange={(e) => setNomeFile(e.target.value)} />
          <button onClick={() => navigate(percorsoBase)}>← Torna indietro</button>
          {puoMappare && <button onClick={() => navigate(`${percorsoBase}/mapping`)}>Torna al mapping</button>}
          <button onClick={nuovoImport}>Nuovo import</button>
          {ultimaEliminazione && (
            <button onClick={annullaEliminazione} title="Rimette al loro posto le righe eliminate per ultime">
              ↩ Annulla eliminazione ({ultimaEliminazione.length} {ultimaEliminazione.length === 1 ? "riga" : "righe"})
            </button>
          )}
          <button className="btn-primario" onClick={scarica} disabled={esportando || colonneObbligatorieMancanti.length > 0}>
            {esportando
              ? "Generazione..."
              : gruppi.length > 1
                ? `Scarica ${gruppi.length} file in un unico .zip`
                : "Scarica file tracciato (.xlsx)"}
          </button>
        </div>
        {gruppi.length > 1 && (
          <p className="testo-secondario">
            Il file contiene {gruppi.length} combinazioni diverse di articolo/variante/colore: l'ERP importa un
            articolo/variante/combinazione per volta, quindi verranno generati {gruppi.length} file .xlsx (uno per
            combinazione), scaricati insieme in un unico file .zip.
          </p>
        )}
        {colonneObbligatorieMancanti.length > 0 && (
          <p className="errore">
            ⚠️ Non puoi ancora scaricare il file: {colonneObbligatorieMancanti.length} colonne obbligatorie hanno righe vuote —{" "}
            {colonneObbligatorieMancanti.map((c) => `${c.nome} (${c.righeVuote.length} righe)`).join(", ")}.
            {puoMappare ? " Compilale a mano qui sotto (evidenziate in rosso) o torna al mapping." : " Compilale a mano qui sotto (evidenziate in rosso)."}
          </p>
        )}
        {righeDuplicate.size > 0 && (
          <p className="testo-secondario">
            🧩⚠️ Trovate {righeDuplicate.size} righe con lo stesso Codice Materiale, Descrizione, Colore, UM e Consumo
            ({gruppiDuplicati.length} gruppi) — non è per forza un errore, ma alla richiesta di scarico verrà chiesta
            conferma.{" "}
            <button className="btn-link" onClick={() => setMostraDuplicati(true)}>Vedi ed eventualmente elimina</button>
          </p>
        )}
        {errore && <p className="errore">{errore}</p>}
        {messaggioCartella && <p className="testo-secondario">{messaggioCartella}</p>}
      </section>

      <section className="riquadro">
        <p className="riga-controlli" style={{ marginTop: 0 }}>
          <button type="button" onClick={() => setMostraFunzioni(true)}>📖 Funzioni</button>
        </p>
        {mostraFunzioni && (
          <LegendaFunzioniModal funzioni={FUNZIONI_REVISIONE} onChiudi={() => setMostraFunzioni(false)} />
        )}
        {righeConAnomaliaCodice.size > 0 && (
          <p className="testo-secondario" style={{ marginTop: 0 }}>
            <button
              onClick={() => setSoloAnomalieCodice((v) => !v)}
              title="Mostra solo le righe segnalate da 🔢⚠️ nelle intestazioni (codici con un numero di cifre diverso dal resto)"
              style={soloAnomalieCodice ? { backgroundColor: "#dc2626", color: "#fff", borderColor: "#dc2626" } : undefined}
            >
              🔢⚠️ {soloAnomalieCodice ? "Mostra tutte le righe" : `Solo righe da correggere (${righeConAnomaliaCodice.size})`}
            </button>{" "}
            <button
              onClick={() => setMostraCorreggiCodici(true)}
              title="Correggi in blocco tutti i codici segnalati — per valore, non riga per riga: una correzione si applica a tutte le righe che hanno quel valore in un colpo solo"
            >
              ✏️ Correggi codici
            </button>
          </p>
        )}
        {mostraCorreggiCodici && (
          <CorreggiCodiciModal
            colonneAnomale={colonneCodiceNonUniforme}
            righe={righeMappate}
            onApplica={applicaCorrezioniCodice}
            onChiudi={() => setMostraCorreggiCodici(false)}
          />
        )}
        {righeDuplicate.size > 0 && (
          <p className="testo-secondario" style={{ marginTop: 0 }}>
            <button
              onClick={() => setSoloDuplicate((v) => !v)}
              title="Mostra solo le righe con lo stesso Codice Materiale, Descrizione Materiale, Codice/Descrizione Colore, UM e Consumo di un'altra riga"
              style={soloDuplicate ? { backgroundColor: "#7f1d1d", color: "#fff", borderColor: "#7f1d1d" } : undefined}
            >
              🧩⚠️ {soloDuplicate ? "Mostra tutte le righe" : `Solo righe duplicate (${righeDuplicate.size})`}
            </button>{" "}
            <button
              onClick={() => setMostraDuplicati(true)}
              title="Vedi i gruppi di righe duplicate e scegli quali eliminare, singolarmente o tutte insieme"
            >
              🧩 Righe duplicate ({gruppiDuplicati.length} gruppi)
            </button>
          </p>
        )}
        {mostraDuplicati && (
          <DuplicatiModal
            gruppi={gruppiDuplicati}
            righe={righeMappate}
            onElimina={eliminaRighe}
            onChiudi={() => setMostraDuplicati(false)}
          />
        )}
        {filtroAttivo && (
          <p className="testo-secondario" style={{ marginTop: 0 }}>
            Mostrando {righeFiltrate.length} di {righeMappate.length} righe (filtro attivo) —{" "}
            <button className="btn-link" onClick={() => { setFiltriColonna({}); setSoloAnomalieCodice(false); setSoloDuplicate(false); }}>azzera filtri</button>
          </p>
        )}
        <div className="scroll-orizzontale scroll-verticale" ref={contenitoreRef}>
          <table className="tabella-revisione">
            <thead>
              <tr>
                <th className="colonna-indice">#</th>
                {colonneMostrate.map((c) => (
                  <th key={c.chiave}>
                    <div style={{ whiteSpace: "nowrap" }}>
                      {c.nome}{" "}
                      {colonneCasoNonUniforme.has(c.chiave) && (
                        <span
                          title={`Questa colonna ha valori sia in MAIUSCOLO che in minuscolo (o misti): usa "AA"/"aa" qui accanto per uniformarli`}
                          style={{ color: "#f59e0b" }}
                        >
                          ⚠️
                        </span>
                      )}{" "}
                      {colonneCodiceNonUniforme.has(c.chiave) && (
                        <span
                          title={`Alcuni valori di questa colonna hanno un numero di cifre diverso dal resto (${colonneCodiceNonUniforme.get(c.chiave).cifreAttese} cifre nella maggior parte delle righe, es. uno zero finale perso da Excel) e/o del testo in più dopo il codice (es. "99.718163 (cotton)"). Le righe evidenziate in rosso qui sotto sono quelle sospette — controllale prima di esportare.`}
                          style={{ color: "#dc2626" }}
                        >
                          🔢⚠️
                        </span>
                      )}{" "}
                      <button
                        type="button"
                        className="btn-link"
                        title={`Copia il valore della prima riga su tutte le altre righe della colonna "${c.nome}"`}
                        onClick={() => riempiColonna(c.chiave)}
                      >
                        ↓ riempi
                      </button>{" "}
                      <button
                        type="button"
                        className="btn-link"
                        title={`Metti in MAIUSCOLO tutti i valori della colonna "${c.nome}"`}
                        onClick={() => trasformaColonna(c.chiave, "maiuscolo")}
                      >
                        AA
                      </button>{" "}
                      <button
                        type="button"
                        className="btn-link"
                        title={`Metti in minuscolo tutti i valori della colonna "${c.nome}"`}
                        onClick={() => trasformaColonna(c.chiave, "minuscolo")}
                      >
                        aa
                      </button>
                    </div>
                    <input
                      type="text"
                      value={filtriColonna[c.chiave] || ""}
                      onChange={(e) => setFiltriColonna((prev) => ({ ...prev, [c.chiave]: e.target.value }))}
                      placeholder="🔍 filtra..."
                      title={`Mostra solo le righe dove "${c.nome}" contiene questo testo`}
                      style={{ width: "100%", boxSizing: "border-box", marginTop: "0.3rem", fontWeight: 400, textTransform: "none" }}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {finestra.altezzaSopra > 0 && (
                <tr aria-hidden="true" style={{ height: finestra.altezzaSopra }}>
                  <td colSpan={colonneMostrate.length + 1} style={{ padding: 0, border: "none" }} />
                </tr>
              )}
              {righeFiltrate.slice(finestra.indicePrimo, finestra.indiceUltimo).map(({ riga, i }, indiceFinestra) => (
                <tr
                  key={chiaveRiga(riga)}
                  ref={indiceFinestra === 0 ? finestra.refPrimaRiga : undefined}
                  style={righeDuplicate.has(i) ? { backgroundColor: "rgba(127, 29, 29, 0.08)" } : undefined}
                >
                  <td className="colonna-indice">
                    {i + 1}{" "}
                    <button
                      className="btn-link"
                      title={righeDuplicate.has(i)
                        ? "Riga duplicata (stesso Codice Materiale, Descrizione, Colore, UM e Consumo di un'altra riga) — elimina solo questa"
                        : "Elimina questa riga (si può annullare)"}
                      onClick={() => eliminaRighe([i])}
                      style={{ color: "var(--cf-danger-text)" }}
                    >
                      🗑️
                    </button>
                  </td>
                  {colonneMostrate.map((c, indiceColonna) => {
                    const vuotaObbligatoria = c.obbligatoria && String(riga[c.chiave] ?? "").trim() === "";
                    const infoCodice = colonneCodiceNonUniforme.get(c.chiave);
                    const infoRigaCodice = infoCodice?.righe.get(i);
                    const anomaliaCodice = !vuotaObbligatoria && infoRigaCodice !== undefined;
                    return (
                      <td key={c.chiave}>
                        <input
                          type="text"
                          ref={refCella(i, c.chiave)}
                          defaultValue={riga[c.chiave] ?? ""}
                          title={
                            vuotaObbligatoria
                              ? "Campo obbligatorio per l'import ERP: non può restare vuoto"
                              : anomaliaCodice
                              ? [
                                  infoRigaCodice.cifreTrovate !== infoCodice.cifreAttese && `${infoRigaCodice.cifreTrovate} cifre invece di ${infoCodice.cifreAttese} — controlla che non manchi una cifra (es. uno zero finale perso da Excel).`,
                                  infoRigaCodice.testoExtra && `Testo in più dopo il codice: "${infoRigaCodice.testoExtra}" — non fa parte del codice articolo.`,
                                ].filter(Boolean).join(" ")
                              : undefined
                          }
                          style={{
                            width: `${larghezzeColonne[c.chiave]}ch`,
                            ...(vuotaObbligatoria ? { border: "1px solid var(--cf-danger-text)", backgroundColor: "var(--cf-danger-bg)" } : {}),
                            ...(anomaliaCodice ? { border: "1px solid #dc2626", backgroundColor: "#fee2e2" } : {}),
                          }}
                          onPaste={(e) => onIncollaCella(e, i, indiceColonna)}
                          onBlur={(e) => {
                            if (e.target.value !== (riga[c.chiave] ?? "")) {
                              modificaCella(i, c.chiave, e.target.value);
                            }
                          }}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
              {finestra.altezzaSotto > 0 && (
                <tr aria-hidden="true" style={{ height: finestra.altezzaSotto }}>
                  <td colSpan={colonneMostrate.length + 1} style={{ padding: 0, border: "none" }} />
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
